import React, { useState, useEffect, useRef } from 'react';
import { CutListProvider, ALGORITHMS, useCutList } from './contexts/CutListContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useIsMobile } from './hooks/useMediaQuery';
import DesktopApp from './desktop/DesktopApp';
import MobileApp from './mobile/MobileApp';
import { UpgradeModal, LicenseKeyModal } from './shared';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import { Monitor, Smartphone, X, Info } from 'lucide-react';
import './App.css';

const DEV_PASSCODE = 'cutcalc2025';

/**
 * Component to enforce algorithm restrictions for free tier
 */
function AlgorithmEnforcer() {
  const { algorithm, setAlgorithm } = useCutList();
  const { canUseAlgorithm, isLoading } = useAuth();

  useEffect(() => {
    // Wait for auth to load, then check if current algorithm is allowed
    if (!isLoading && !canUseAlgorithm(algorithm)) {
      setAlgorithm(ALGORITHMS.BOTTOM_LEFT);
    }
  }, [algorithm, canUseAlgorithm, isLoading, setAlgorithm]);

  return null;
}

/**
 * Hidden dev toggle hook - tap 5 times to trigger
 */
function useDevToggle(onTrigger) {
  const tapCountRef = useRef(0);
  const tapTimeoutRef = useRef(null);

  const handleTap = () => {
    tapCountRef.current += 1;

    // Reset tap count after 2 seconds of no taps
    if (tapTimeoutRef.current) clearTimeout(tapTimeoutRef.current);
    tapTimeoutRef.current = setTimeout(() => {
      tapCountRef.current = 0;
    }, 2000);

    // Trigger on 5 taps
    if (tapCountRef.current >= 5) {
      tapCountRef.current = 0;
      onTrigger();
    }
  };

  return handleTap;
}

/**
 * Passcode modal for dev access
 */
function DevPasscodeModal({ isOpen, onClose }) {
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState(false);
  const { isPro, toggleProStatus, resetUsage } = useAuth();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (passcode === DEV_PASSCODE) {
      toggleProStatus();
      setPasscode('');
      setError(false);
      onClose();
    } else {
      setError(true);
      setPasscode('');
    }
  };

  const handleReset = () => {
    if (passcode === DEV_PASSCODE) {
      resetUsage();
      setPasscode('');
      setError(false);
      onClose();
    } else {
      setError(true);
      setPasscode('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-xl max-w-sm w-full p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900">Dev Access</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-4 text-sm">
          <span className="text-gray-600">Current status: </span>
          <span className={`font-semibold ${isPro ? 'text-green-600' : 'text-gray-500'}`}>
            {isPro ? 'PRO' : 'FREE'}
          </span>
        </div>

        <form onSubmit={handleSubmit}>
          <input
            type="password"
            value={passcode}
            onChange={(e) => {
              setPasscode(e.target.value);
              setError(false);
            }}
            placeholder="Enter passcode"
            className={`w-full px-4 py-3 border rounded-lg mb-3 focus:outline-none focus:ring-2 ${
              error
                ? 'border-red-300 focus:ring-red-200'
                : 'border-gray-300 focus:ring-accent-200 focus:border-accent-400'
            }`}
            autoFocus
          />
          {error && (
            <p className="text-red-500 text-sm mb-3">Incorrect passcode</p>
          )}

          <div className="flex gap-2">
            <button
              type="submit"
              className="flex-1 py-2 bg-accent-400 text-white rounded-lg font-medium hover:bg-accent-500"
            >
              Toggle Pro
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="flex-1 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
            >
              Reset Usage
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * About modal - credits and legal info
 */
function AboutModal({ isOpen, onClose }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-xl max-w-sm w-full p-6 shadow-2xl max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900">About CutCalc</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4 text-sm text-gray-600">
          <div>
            <h3 className="font-semibold text-gray-800 mb-1">Created by</h3>
            <p>Zak Ziebell & Walker Nosworthy</p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-800 mb-1">Version</h3>
            <p>1.0.0</p>
          </div>

          <hr className="border-gray-200" />

          <div className="text-xs text-gray-500 space-y-2">
            <h3 className="font-semibold text-gray-700">Disclaimer</h3>
            <p>
              This software is provided "as is" without warranty of any kind, express or implied.
              The creators are not liable for any damages or losses arising from the use of this software.
            </p>
            <p>
              Cut layouts are generated algorithmically and should be verified before cutting materials.
              Always measure twice, cut once. The user assumes all responsibility for material usage and cuts made.
            </p>
            <p>
              CutCalc is intended as a planning tool only. Professional verification is recommended for critical projects.
            </p>
          </div>

          <hr className="border-gray-200" />

          <p className="text-xs text-gray-400 text-center">
            &copy; {new Date().getFullYear()} CutCalc. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * App Root - Handles device detection and mode switching
 * Wraps everything in CutListProvider for shared state
 */
function AppContent() {
  const isMobileDevice = useIsMobile();
  const [forcedMode, setForcedMode] = useState(null); // 'desktop' | 'mobile' | null
  const [showDevModal, setShowDevModal] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);

  // Dev toggle - tap 5 times to reveal (development only)
  const handleDevTap = useDevToggle(() => {
    if (process.env.NODE_ENV === 'development') {
      setShowDevModal(true);
    }
  });

  // Determine which view to show
  const showMobile = forcedMode === 'mobile' || (forcedMode === null && isMobileDevice);

  return (
    <div className="relative">
      {/* Render appropriate view */}
      {showMobile ? (
        <div>
          <MobileApp onShowAbout={() => setShowAboutModal(true)} onSwitchToDesktop={() => { handleDevTap(); setForcedMode('desktop'); }} />
        </div>
      ) : (
        <DesktopApp onShowAbout={() => setShowAboutModal(true)} onSwitchToMobile={() => { handleDevTap(); setForcedMode('mobile'); }} />
      )}

      {/* Utility buttons - different placement for mobile vs desktop */}
      {/* Mobile utility buttons moved to MobileApp nav bar */}

      {/* Dev passcode modal */}
      <DevPasscodeModal
        isOpen={showDevModal}
        onClose={() => setShowDevModal(false)}
      />

      {/* About modal */}
      <AboutModal
        isOpen={showAboutModal}
        onClose={() => setShowAboutModal(false)}
      />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <CutListProvider>
        <AlgorithmEnforcer />
        <AppContent />
        <UpgradeModal />
        <LicenseKeyModal />
        <PWAInstallPrompt />
      </CutListProvider>
    </AuthProvider>
  );
}

export default App;
